import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../services/apiClient';
import { useAuth } from './AuthContext';
import { USER_ROLES } from '../constants/roles';

const BranchContext = createContext();

export const useBranch = () => {
  const context = useContext(BranchContext);
  if (!context) {
    throw new Error('useBranch must be used within a BranchProvider');
  }
  return context;
};

function isBranchManager(user) {
  if (!user?.roles?.length) return false;
  return user.roles.some((r) => (r?.name || r) === USER_ROLES.BRANCH_MANAGER);
}

export const BranchProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [restaurants, setRestaurants] = useState([]);
  const [allBranches, setAllBranches] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [listenAllMode, setListenAllModeState] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [branchSelectModalVisible, setBranchSelectModalVisible] = useState(false);

  const fetchRestaurants = useCallback(async (listenAll) => {
    try {
      const response = await apiClient.get('/restaurant/owner/my-restaurants');
      if (response.data.success) {
        const restaurantsList = response.data.restaurants || [];
        setRestaurants(restaurantsList);

        const storedRestaurantId = localStorage.getItem('selectedRestaurantId');
        if (!listenAll && restaurantsList.length > 0) {
          if (storedRestaurantId) {
            const restoredRestaurant = restaurantsList.find(r => r._id === storedRestaurantId);
            setSelectedRestaurant(restoredRestaurant || restaurantsList[0]);
          } else {
            setSelectedRestaurant(restaurantsList[0]);
          }
        }
        return restaurantsList;
      }
      return [];
    } catch (err) {
      
      setError(err?.response?.data?.message || 'Failed to load restaurants');
      return [];
    }
  }, []);

  const fetchAllBranches = useCallback(async (listenAll) => {
    try {
      const response = await apiClient.get('/restaurant/owner/all-branches');
      if (response.data.success) {
        const branchesList = response.data.branches || [];
        setAllBranches(branchesList);

        const storedBranchId = localStorage.getItem('selectedBranchId');
        if (!listenAll && branchesList.length > 0) {
          if (storedBranchId) {
            const restoredBranch = branchesList.find(b => b._id === storedBranchId);
            setSelectedBranch(restoredBranch || branchesList[0]);
          } else {
            setSelectedBranch(branchesList[0]);
          }
        }
        return branchesList;
      }
      return [];
    } catch (err) {
      
      setError(err?.response?.data?.message || 'Failed to load branches');
      return [];
    }
  }, []);

  const fetchManagerBranches = useCallback(async () => {
    try {
      const response = await apiClient.get('/restaurant-branches', { params: { limit: 50, page: 1 } });
      const branchesList = response.data?.branches || [];
      setAllBranches(branchesList);

      if (branchesList.length > 0) {
        const defaultBranch = branchesList[0];
        setSelectedBranch(defaultBranch);
        setSelectedRestaurant(defaultBranch.restaurant || null);
        const uniqueRestaurants = [];
        const seen = new Set();
        branchesList.forEach((b) => {
          const r = b.restaurant;
          const id = r?._id || r;
          if (id && !seen.has(id)) {
            seen.add(id);
            uniqueRestaurants.push(typeof r === 'object' ? r : { _id: id });
          }
        });
        setRestaurants(uniqueRestaurants.length > 0 ? uniqueRestaurants : (defaultBranch.restaurant ? [defaultBranch.restaurant] : []));
        localStorage.setItem('selectedBranchId', defaultBranch._id);
        if (defaultBranch.restaurant) {
          localStorage.setItem('selectedRestaurantId', defaultBranch.restaurant._id || defaultBranch.restaurant);
        }
      } else {
        setSelectedBranch(null);
        setSelectedRestaurant(null);
      }
      return branchesList;
    } catch (err) {
      
      setError(err?.response?.data?.message || 'Failed to load branch');
      return [];
    }
  }, []);

  const loadPersistedState = useCallback(async () => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);

      if (isBranchManager(user)) {
        localStorage.setItem('listenAllMode', 'false');
        setListenAllModeState(false);
        await fetchManagerBranches();
      } else {
        let listenAll = false;
        try {
          const settingsRes = await apiClient.get('/user/settings');
          if (settingsRes.data?.settings?.listenAllRestaurants === true) {
            listenAll = true;
            localStorage.setItem('listenAllMode', 'true');
          } else {
            localStorage.setItem('listenAllMode', 'false');
          }
        } catch {
          const storedListenAll = localStorage.getItem('listenAllMode');
          listenAll = storedListenAll === 'true';
        }
        setListenAllModeState(listenAll);

        await Promise.all([
          fetchRestaurants(listenAll),
          fetchAllBranches(listenAll)
        ]);
      }
    } catch (err) {
      
      setError(err?.message || 'Failed to load');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user, fetchRestaurants, fetchAllBranches, fetchManagerBranches]);

  useEffect(() => {
    loadPersistedState();
  }, [loadPersistedState]);

  const selectRestaurant = async (restaurant) => {
    try {
      setSelectedRestaurant(restaurant);
      if (restaurant) {
        localStorage.setItem('selectedRestaurantId', restaurant._id);
        const restaurantBranches = allBranches.filter(b =>
          b.restaurant?._id === restaurant._id || b.restaurant === restaurant._id
        );
        if (restaurantBranches.length > 0) {
          await selectBranch(restaurantBranches[0]);
        }
      } else {
        localStorage.removeItem('selectedRestaurantId');
      }
    } catch (error) {
      
    }
  };

  const selectBranch = async (branch) => {
    try {
      setSelectedBranch(branch);
      if (branch) {
        localStorage.setItem('selectedBranchId', branch._id);
      } else {
        localStorage.removeItem('selectedBranchId');
      }
    } catch (error) {
      
    }
  };

  useEffect(() => {
    if (!listenAllMode && restaurants.length > 0 && !selectedRestaurant) {
      selectRestaurant(restaurants[0]);
    }
  }, [listenAllMode, restaurants.length, selectedRestaurant, selectRestaurant]);

  const setListenAllMode = async (enabled) => {
    try {
      localStorage.setItem('listenAllMode', enabled.toString());
      try {
        await apiClient.put('/user/settings', {
          listenAllRestaurants: enabled
        });
      } catch (apiError) {
        
      }

      if (enabled) {
        setSelectedRestaurant(null);
        setSelectedBranch(null);
        localStorage.removeItem('selectedRestaurantId');
        localStorage.removeItem('selectedBranchId');
      }
      setListenAllModeState(enabled);
    } catch (error) {
      
    }
  };

  const getSelectedRestaurantBranches = useCallback(() => {
    if (!selectedRestaurant) return [];
    return allBranches.filter(b =>
      b.restaurant?._id === selectedRestaurant._id ||
      b.restaurant === selectedRestaurant._id
    );
  }, [selectedRestaurant, allBranches]);

  const refreshBranches = useCallback(async () => {
    if (isBranchManager(user)) {
      await fetchManagerBranches();
      return;
    }
    const listenAll = localStorage.getItem('listenAllMode') === 'true';
    await Promise.all([fetchRestaurants(listenAll), fetchAllBranches(listenAll)]);
  }, [user, fetchRestaurants, fetchAllBranches, fetchManagerBranches]);

  const restaurantName = selectedRestaurant?.name || selectedBranch?.restaurant?.name || restaurants[0]?.name || null;
  const branches = restaurants.length <= 1 ? allBranches : getSelectedRestaurantBranches();
  const isBranchSelected = useCallback((id) => selectedBranch?._id === id, [selectedBranch]);

  const requestBranchSelect = useCallback(() => {
    setBranchSelectModalVisible(true);
  }, []);

  const value = {
    restaurants,
    allBranches,
    branches,
    selectedRestaurant,
    selectedBranch,
    listenAllMode,
    isLoading,
    error,
    restaurantName,
    setListenAllMode,
    selectRestaurant,
    selectBranch,
    switchBranch: selectBranch,
    refreshBranches,
    isBranchSelected,
    fetchRestaurants,
    fetchAllBranches,
    getSelectedRestaurantBranches,
    branchSelectModalVisible,
    setBranchSelectModalVisible,
    requestBranchSelect,
  };

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
};

export default BranchContext;